import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/components/ui/use-toast';

const projectTemplates = [
  { value: 'client-onboarding', label: 'Client Onboarding' },
  { value: 'ai-agent-dev', label: 'AI Agent Development' },
  { value: 'software-dev', label: 'Software Development' },
  { value: 'research-project', label: 'Internal R&D Project' },
];

const CreateProjectDialog = ({ isOpen, onClose, onSave }) => {
  const [projectName, setProjectName] = useState('');
  const [projectDescription, setProjectDescription] = useState('');
  const [projectTemplate, setProjectTemplate] = useState('');
  const { toast } = useToast();

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!projectName.trim()) {
      toast({ title: "Validation Error", description: "Project name is required.", variant: "destructive" });
      return;
    }
    if (!projectTemplate) {
      toast({ title: "Validation Error", description: "Please select a project template.", variant: "destructive" });
      return;
    }
    onSave({ name: projectName, description: projectDescription, template: projectTemplate });
    setProjectName('');
    setProjectDescription('');
    setProjectTemplate('');
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[480px] bg-slate-800 border-slate-700 glassmorphism">
        <DialogHeader>
          <DialogTitle className="text-2xl text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-500">Create New Project</DialogTitle>
          <DialogDescription className="text-slate-400">
            Fill in the details below to start a new project.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="grid gap-6 py-4">
          <div className="grid gap-2">
            <Label htmlFor="projectName">Project Name</Label>
            <Input
              id="projectName"
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              placeholder="E.g., NextGen AI Chatbot"
              className="bg-slate-700 border-slate-600 text-slate-100"
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="projectDescription">Description (Optional)</Label>
            <Textarea
              id="projectDescription"
              value={projectDescription}
              onChange={(e) => setProjectDescription(e.target.value)}
              placeholder="A brief overview of the project goals and scope."
              className="bg-slate-700 border-slate-600 text-slate-100"
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="projectTemplate">Project Template</Label>
            <Select value={projectTemplate} onValueChange={setProjectTemplate}>
              <SelectTrigger id="projectTemplate" className="w-full bg-slate-700 border-slate-600 text-slate-100">
                <SelectValue placeholder="Select a template" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700 text-slate-100">
                {projectTemplates.map((template) => (
                  <SelectItem key={template.value} value={template.value} className="hover:bg-slate-700 focus:bg-slate-700">
                    {template.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} className="text-slate-300 border-slate-600 hover:bg-slate-700">
              Cancel
            </Button>
            <Button type="submit" className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white">
              Create Project
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateProjectDialog;